import React, { useState, useEffect } from 'react';
import { XwebaLogo } from './XwebaLogo';
import { Currency } from '../types';
import { Sparkles, Calendar, Menu, X, ArrowUpRight, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface NavbarProps {
  currency: Currency;
  onCurrencyChange: (curr: Currency) => void;
  onOpenBooking: () => void;
  onOpenAudit: () => void;
  onHomeClick?: () => void;
  onOpenContact?: () => void;
  onNavigateToServices?: () => void;
  onNavigateToPackages?: () => void;
  onNavigateToFaq?: () => void;
  onNavigateToBlog?: () => void;
  onNavigateToContact?: () => void;
  currentView?: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  currency,
  onCurrencyChange,
  onOpenBooking,
  onOpenAudit,
  onHomeClick,
  onOpenContact,
  onNavigateToServices,
  onNavigateToPackages,
  onNavigateToFaq,
  onNavigateToBlog,
  onNavigateToContact,
  currentView = 'home',
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, callback?: () => void) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return; // Allow native new-tab / window behavior
    }
    if (callback) {
      e.preventDefault();
      callback();
    }
  };

  const handleServicesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e, onNavigateToServices);
  };

  const handlePackagesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e, onNavigateToPackages);
  };

  const handleAuditClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e, onOpenAudit);
  };

  const handleFaqClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e, onNavigateToFaq);
  };

  const handleBlogClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e, onNavigateToBlog);
  };

  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    handleNavClick(e, onNavigateToContact || onOpenContact);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-xs py-3'
          : 'bg-white/90 backdrop-blur-sm border-b border-slate-100 py-3.5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          {/* Zone 1: Logo Wordmark */}
          <a
            href="/"
            onClick={(e) => handleNavClick(e, onHomeClick)}
            className="flex items-center gap-2 group shrink-0 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#F26522] rounded-lg"
          >
            <XwebaLogo size="md" withTagline={true} />
          </a>

          {/* Zone 2: Dedicated Independent Page Navigation Links with animated active indicator */}
          <nav className="hidden xl:flex items-center gap-2 text-sm font-medium text-slate-700 shrink-0" aria-label="Main Navigation">
            {[
              { path: '/services', label: 'Services', isCurrent: currentView === 'services' || currentView === 'ai-websites' || currentView === 'ai-search-visibility' || currentView === 'ai-business-automation', onClick: handleServicesClick },
              { path: '/packages', label: 'Packages & WaaS', isCurrent: currentView === 'packages', onClick: handlePackagesClick },
              { path: '/xwebaudit', label: 'Free Xwebaudit', isCurrent: currentView === 'xwebaudit', onClick: handleAuditClick },
              { path: '/faq', label: 'FAQ', isCurrent: currentView === 'faq', onClick: handleFaqClick },
              { path: '/blog', label: 'Digital Pulse', isCurrent: currentView === 'blog' || currentView === 'blog-post', onClick: handleBlogClick },
              { path: '/contact', label: 'Contact', isCurrent: currentView === 'contact', onClick: handleContactClick },
            ].map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={link.onClick}
                className={`transition-colors relative py-2.5 px-3 min-h-[44px] inline-flex items-center cursor-pointer whitespace-nowrap shrink-0 ${
                  link.isCurrent
                    ? 'text-[#0B2D52] font-bold'
                    : 'text-slate-600 hover:text-[#0B2D52]'
                }`}
              >
                <span>{link.label}</span>
                {link.isCurrent && (
                  <motion.span
                    layoutId="navbar-active-pill"
                    className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#F26522] rounded-full"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </a>
            ))}
          </nav>

          {/* Zone 3: Right Controls & Primary CTA with interactive animations */}
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            {/* Currency Switcher with smooth sliding motion pill */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 text-xs font-semibold border border-slate-200 relative">
              <button
                type="button"
                onClick={() => onCurrencyChange('USD')}
                className={`relative px-3.5 py-2 min-h-[40px] min-w-[50px] rounded-lg transition-colors cursor-pointer z-10 flex items-center justify-center ${
                  currency === 'USD' ? 'text-[#0B2D52] font-bold' : 'text-slate-500 hover:text-slate-800'
                }`}
                aria-label="Display prices in US Dollars"
              >
                {currency === 'USD' && (
                  <motion.span
                    layoutId="currency-active-pill"
                    className="absolute inset-0 bg-white rounded-lg shadow-xs -z-10"
                    transition={{ type: 'spring', stiffness: 450, damping: 30 }}
                  />
                )}
                <span>USD ($)</span>
              </button>
              <button
                type="button"
                onClick={() => onCurrencyChange('KES')}
                className={`relative px-3.5 py-2 min-h-[40px] min-w-[50px] rounded-lg transition-colors cursor-pointer z-10 flex items-center justify-center ${
                  currency === 'KES' ? 'text-[#0B2D52] font-bold' : 'text-slate-500 hover:text-slate-800'
                }`}
                aria-label="Display prices in Kenyan Shillings"
              >
                {currency === 'KES' && (
                  <motion.span
                    layoutId="currency-active-pill"
                    className="absolute inset-0 bg-white rounded-lg shadow-xs -z-10"
                    transition={{ type: 'spring', stiffness: 450, damping: 30 }}
                  />
                )}
                <span>KSh (KES)</span>
              </button>
            </div>

            {/* Direct WhatsApp Action with spring transition */}
            <motion.a
              href="https://wa.me/254725724175"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.025, y: -0.5 }}
              whileTap={{ scale: 0.975 }}
              transition={{ type: 'spring', stiffness: 450, damping: 25 }}
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 min-h-[44px] text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all border border-emerald-200 shadow-2xs whitespace-nowrap btn-transition"
              title="Chat on WhatsApp: +254 725 724 175"
            >
              <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
              <span>WhatsApp</span>
            </motion.a>

            {/* Primary CTA with hover & tap spring transition */}
            <motion.button
              type="button"
              onClick={onOpenBooking}
              whileHover={{ scale: 1.025, y: -0.5 }}
              whileTap={{ scale: 0.975 }}
              transition={{ type: 'spring', stiffness: 450, damping: 25 }}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 min-h-[44px] text-xs font-bold text-white bg-[#F26522] hover:bg-[#ea580c] active:scale-98 rounded-xl shadow-xs shadow-orange-500/20 transition-all cursor-pointer whitespace-nowrap shrink-0 btn-transition"
            >
              <span>Book Strategy Call</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </motion.button>
          </div>

          {/* Mobile Menu Toggle & Compact Currency */}
          <div className="flex items-center gap-2.5 xl:hidden">
            <div className="flex sm:hidden items-center gap-1 bg-slate-100 rounded-xl p-1 text-xs font-semibold border border-slate-200 relative">
              <button
                type="button"
                onClick={() => onCurrencyChange('USD')}
                className={`relative px-3 py-2 min-h-[42px] min-w-[44px] rounded-lg cursor-pointer z-10 ${currency === 'USD' ? 'text-[#0B2D52] font-bold' : 'text-slate-500'}`}
                aria-label="Switch currency to US Dollars"
              >
                {currency === 'USD' && (
                  <motion.span
                    layoutId="mobile-currency-pill"
                    className="absolute inset-0 bg-white rounded-lg shadow-xs -z-10"
                    transition={{ type: 'spring', stiffness: 450, damping: 30 }}
                  />
                )}
                <span>$</span>
              </button>
              <button
                type="button"
                onClick={() => onCurrencyChange('KES')}
                className={`relative px-3 py-2 min-h-[42px] min-w-[44px] rounded-lg cursor-pointer z-10 ${currency === 'KES' ? 'text-[#0B2D52] font-bold' : 'text-slate-500'}`}
                aria-label="Switch currency to Kenyan Shillings"
              >
                {currency === 'KES' && (
                  <motion.span
                    layoutId="mobile-currency-pill"
                    className="absolute inset-0 bg-white rounded-lg shadow-xs -z-10"
                    transition={{ type: 'spring', stiffness: 450, damping: 30 }}
                  />
                )}
                <span>KSh</span>
              </button>
            </div>
            <motion.button
              type="button"
              whileTap={{ scale: 0.94 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-slate-700 hover:text-[#0B2D52] rounded-xl border border-slate-200 cursor-pointer"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Dropdown with AnimatePresence */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -6 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -6 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="xl:hidden mt-3 pt-3 border-t border-slate-200 pb-4 space-y-3 overflow-hidden"
            >
              <div className="flex flex-col gap-1.5 text-sm font-medium text-slate-800">
                <a
                  href="/services"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleServicesClick(e);
                  }}
                  className={`py-3 px-4 min-h-[48px] flex items-center rounded-xl text-left cursor-pointer transition-colors ${
                    currentView === 'services' || currentView === 'ai-websites' || currentView === 'ai-search-visibility' || currentView === 'ai-business-automation'
                      ? 'bg-slate-100 font-bold text-[#0B2D52]'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  Services
                </a>
                <a
                  href="/packages"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handlePackagesClick(e);
                  }}
                  className={`py-3 px-4 min-h-[48px] flex items-center rounded-xl text-left cursor-pointer transition-colors ${
                    currentView === 'packages' ? 'bg-slate-100 font-bold text-[#0B2D52]' : 'hover:bg-slate-50'
                  }`}
                >
                  Packages & WaaS
                </a>
                <a
                  href="/xwebaudit"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleAuditClick(e);
                  }}
                  className={`py-3 px-4 min-h-[48px] rounded-xl text-left cursor-pointer transition-colors flex items-center justify-between ${
                    currentView === 'xwebaudit' ? 'bg-orange-50 text-[#F26522] font-bold' : 'hover:bg-slate-50 text-slate-800'
                  }`}
                >
                  <span>Free Xwebaudit</span>
                  <Sparkles className="w-4 h-4 text-[#F26522]" />
                </a>
                <a
                  href="/faq"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleFaqClick(e);
                  }}
                  className={`py-3 px-4 min-h-[48px] flex items-center rounded-xl text-left cursor-pointer transition-colors ${
                    currentView === 'faq' ? 'bg-slate-100 font-bold text-[#0B2D52]' : 'hover:bg-slate-50'
                  }`}
                >
                  FAQ
                </a>
                <a
                  href="/blog"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleBlogClick(e);
                  }}
                  className={`py-3 px-4 min-h-[48px] flex items-center rounded-xl text-left cursor-pointer transition-colors ${
                    currentView === 'blog' ? 'bg-slate-100 font-bold text-[#0B2D52]' : 'hover:bg-slate-50'
                  }`}
                >
                  Digital Pulse
                </a>
                <a
                  href="/contact"
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    handleContactClick(e);
                  }}
                  className={`py-3 px-4 min-h-[48px] flex items-center rounded-xl text-left cursor-pointer transition-colors ${
                    currentView === 'contact' ? 'bg-slate-100 font-bold text-[#0B2D52]' : 'hover:bg-slate-50'
                  }`}
                >
                  Contact
                </a>
              </div>

              <div className="pt-2 flex flex-col gap-2.5 border-t border-slate-100">
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenBooking();
                  }}
                  className="w-full py-3 px-4 min-h-[48px] text-xs font-bold text-white bg-[#F26522] hover:bg-[#ea580c] rounded-xl flex items-center justify-center gap-2 cursor-pointer btn-transition shadow-xs"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Book Strategy Call</span>
                </motion.button>

                <a
                  href="https://wa.me/254725724175"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 px-4 min-h-[48px] text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-xl flex items-center justify-center gap-2 border border-emerald-200"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                  <span>WhatsApp: +254 725 724 175</span>
                </a>

                <div className="text-center text-[11px] text-slate-500 pt-1">
                  info@xweba.com · www.xweba.com
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
